"use client";

import { cn } from "@/lib/utils";

export interface LegendScaleStep {
  /** Step label, e.g. "1" or "Expert". */
  label: string;
  /** Per-step colour. Falls back to the scale-wide `color`. */
  color?: string;
}

export interface LegendScaleProps {
  /** Leading caption, e.g. "Level" or "Correlation". */
  caption?: string;
  /** Discrete form: one entry per step. */
  steps?: LegendScaleStep[];
  /** Shared marker colour for steps without their own. */
  color?: string;
  /** Continuous form: any CSS background value for the ramp. */
  gradient?: string;
  /** Continuous form: label shown before the ramp. */
  startLabel?: string;
  /** Continuous form: label shown after the ramp. */
  endLabel?: string;
  /** Container class name */
  className?: string;
  /** Caption class name. Default: "text-xs text-legend-muted-foreground" */
  captionClassName?: string;
  /** Step / end label class name. Default: "text-xs tabular-nums" */
  labelClassName?: string;
  /** Step marker class name. Default: "h-2.5 w-2.5 rounded-full" */
  markerClassName?: string;
  /** Continuous ramp class name. Default: "h-2 min-w-16 flex-1 rounded-full" */
  rampClassName?: string;
}

export function LegendScale({
  caption,
  steps,
  color,
  gradient,
  startLabel,
  endLabel,
  className,
  captionClassName = "text-xs text-legend-muted-foreground",
  labelClassName = "text-xs tabular-nums",
  markerClassName = "h-2.5 w-2.5 rounded-full",
  rampClassName = "h-2 min-w-16 flex-1 rounded-full",
}: LegendScaleProps) {
  const labelClasses = cn(
    "leading-tight text-legend-foreground [overflow-wrap:anywhere]",
    labelClassName,
  );

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      {caption && (
        <span className={cn("shrink-0", captionClassName)}>{caption}</span>
      )}

      {gradient ? (
        <>
          {startLabel && (
            <span className={cn("shrink-0", labelClasses)}>{startLabel}</span>
          )}
          <div className={cn(rampClassName)} style={{ background: gradient }} />
          {endLabel && (
            <span className={cn("shrink-0", labelClasses)}>{endLabel}</span>
          )}
        </>
      ) : (
        <div className="flex shrink-0 items-center gap-1.5">
          {steps?.map((step, index) => (
            <div
              key={`${step.label}-${index}`}
              className="flex shrink-0 items-center gap-1"
            >
              <span
                className={cn("inline-block shrink-0", markerClassName)}
                style={{ backgroundColor: step.color ?? color }}
              />
              <span className={labelClasses}>{step.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

LegendScale.displayName = "LegendScale";
